import React, { FunctionComponent } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_all } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import StarBorderPurple500 from '@mui/icons-material/StarBorderPurple500';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

import {
  Box,
  Button as Btn,
  DialogActions,
  Divider,
  Grid,
  MenuItem,
  Paper,
} from '@mui/material';

import {
  Button,
  Dt,
  Select,
  Typography
} from '../../../../components/v2/styled';

import { DiaryDay } from '../../../../models/DiaryDay';
import { DiaryFeedback } from '../../../../models/DiaryFeedback';
import { User } from '../../../../models/User';

const { DateTime } = require("luxon");

import HelpersPractice from '../../../../actions/helpers/practice';
import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';
import { Base } from '../../../../components/v2/dialogs/Base';

type Props = {
  object: User;
  mutationCustomAction: any;
  setPremiumDiaryEatOpen: (b:boolean) => void;
  setPremiumDiaryMoveOpen: (b:boolean) => void;
  groupsEatEditing: boolean;
  setGroupsEatEditing: (b:boolean) => void;
  groupsMoveEditing: boolean;
  setGroupsMoveEditing: (b:boolean) => void;
  newEatGroup?: string;
  setNewEatGroup: (s:string) => void;
  newMoveGroup?: string;
  setNewMoveGroup: (s:string) => void;
}

// The widget's body rows are coach-configurable (show/hide + order) via the
// `diary_small` + `diary_small_order` settings on the coach — the goto_links
// pattern, with a three-state model (module-gated rows turn 'disabled').
// Rows render in saved order when 'active':
// - eat_group / move_group : the diary-group rows (adjust dialog + premium
//   overlay) — gated on their diary module.
// - last_entry             : the "last entry" date from module_status.
// - daynote_client         : the client's most recent own note on a diary day
//   (client_note, hidden-from-this-practice respected).
// - daynote_coach          : the most recent coach feedback on the diary days
//   (the feedbacks assoc for this practice — internal or external, both are
//   coach-facing; the coach.diary.feedbacks dialog's data).
// The CTA follows the module's shared pattern instead: a small text button
// top-right in the title row, gated by the composite's `cta` key (default
// on), NOT orderable. The two daynote rows share one query: the last 30
// diary days (day DESC), scanned client-side for the most recent note.
export const CoachUserWidgetsDiarySmall: FunctionComponent<Props> = ({
  object,
  mutationCustomAction,
  setPremiumDiaryEatOpen, setPremiumDiaryMoveOpen,
  groupsEatEditing, setGroupsEatEditing,
  groupsMoveEditing, setGroupsMoveEditing,
  newEatGroup, setNewEatGroup,
  newMoveGroup, setNewMoveGroup
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);

  let is_nutri_diary_eat = HelpersPractice.practice_modules_v3({practice: currentPractice, check: 'nutri_diary_eat'});
  let is_nutri_diary_move = HelpersPractice.practice_modules_v3({practice: currentPractice, check: 'nutri_diary_move'});

  let group_eat = Object.keys((object?.full_eat_groups || {})).filter(ff => (object?.full_eat_groups || {})[ff].practices.filter(fff => fff[0] === currentPractice?.id).length > 0)[0];
  let eat_group_name = ((object?.full_eat_groups || {})[group_eat] || {}).profile?.name;
  let group_move = Object.keys((object?.full_move_groups || {})).filter(ff => (object?.full_move_groups || {})[ff].practices.filter(fff => fff[0] === currentPractice?.id).length > 0)[0];
  let move_group_name = ((object?.full_move_groups || {})[group_move] || {}).profile?.name;

  // Coach-level widget settings: composite { cta, items } + sibling order key.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.diarySmallValueKey];
  const savedOrder = widgetSettings?.[HelpersClientWidgets.diarySmallOrderKey];
  const orderedItems = HelpersClientWidgets.getOrderedDiarySmallItems(savedOrder, cfg?.items, currentPractice);
  const activeItems = orderedItems.filter((i) => i.state === 'active');
  const showCta = HelpersClientWidgets.getDiarySmallCtaEnabled(cfg);

  // Which rows are active (queries only fetch for rendered rows).
  const isActive = (key: string) => activeItems.some((i) => i.key === key);
  const needDaynotes = isActive('daynote_client') || isActive('daynote_coach');

  // Last 30 diary days, newest first, for the two daynote rows. The feedbacks
  // assoc must be named in BOTH fields and sub_fields to come back.
  // Scanned client-side: the client note is the first non-empty client_note
  // not hidden from this practice; the coach note is the latest feedback
  // (created_at) from this practice with a comment (internal or external).
  const {
    data: noteDays,
  } = useQuery({
    queryKey: ["diary", "diary_day_notes", object?.id],
    queryFn: () =>
      fetch_all<DiaryDay>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'diary_day',
          fields: [
            'id', 'day', 'client_note', 'hide_practices', 'feedbacks'
          ],
          sub_fields: {
            feedbacks: ['id', 'practice_id', 'comment', 'is_internal', 'created_at']
          },
          per_page: 30,
          page: 0,
          order: 'day DESC',
          filter: {
            advanced: {
              user_id: object?.id
            }
          }
        },
        login
      ),
    enabled: needDaynotes && !!object?.id,
  });

  // Resolve the most recent client note (client_note not hidden from this
  // practice) and coach feedback (latest created_at for this practice) from
  // the scanned days. Returns { note, day } / { comment, created_at }.
  // Caption dates are pre-formatted strings (t() interpolations can't take
  // a React node — it would stringify to [object Object]).
  const fmtDay = (iso?: string) => {
    return !!iso ? DateTime.fromISO(iso).setZone("Europe/Brussels").toFormat("DDD", {locale: i18n.resolvedLanguage || 'nl'}) : '';
  };
  const lastClientNote = (() => {
    const days = noteDays || [];
    for (const day of days) {
      const hidden = (day.hide_practices || '').split(",").indexOf(currentPractice?.id?.toString() || '') > -1;
      if (!!day.client_note && !hidden) {
        return { note: day.client_note, day: day.day, dayLabel: fmtDay(day.day) };
      }
    }
    return undefined;
  })();

  const lastCoachFeedback = (() => {
    const candidates: DiaryFeedback[] = [];
    (noteDays || []).forEach((day) => {
      (day.feedbacks || []).forEach((feedback) => {
        if (feedback.practice_id === currentPractice?.id && !!feedback.comment) {
          candidates.push(feedback);
        }
      });
    });
    if (candidates.length < 1) return undefined;
    return candidates.reduce((a, b) => {
      const aT = DateTime.fromISO(a.created_at || '');
      const bT = DateTime.fromISO(b.created_at || '');
      return (aT < bT) ? b : a;
    });
  })();
  const lastCoachFeedbackLabel = !!lastCoachFeedback ? fmtDay(lastCoachFeedback.created_at) : '';

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    {/* CTA: small text button top-right, absolute in the title row (the
        module's shared pattern). Gated by the coach's `cta` setting; the
        premium lock uses the v2 Button's built-in outlined + star treatment
        (locked when neither diary module is on). */}
    {showCta && <Button
      label={t("coach.general.actions.to_diary", "Naar het dagboek")}
      id="goto_diary"
      text
      size="small"
      premiumLocked={!(is_nutri_diary_eat || is_nutri_diary_move)}
      startIcon={<ArrowRightAltIcon />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => {
        if (is_nutri_diary_eat || is_nutri_diary_move) {
          navigate(`/user/${object.id}/diary`);
        } else {
          setPremiumDiaryEatOpen(true);
        }
      }}
    />}

    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.diary")}</Typography>
        <Divider />
      </Grid>

      {/* Rows render in the coach's saved order, 'active' items only. */}
      {activeItems.map((item) => {
        if (item.key === 'eat_group') {
          return <Grid key={item.key} item xs={12} sx={{position: 'relative'}}>
            <Box sx={{
              zIndex: 1200,
              position: 'absolute',
              top: 8,
              left: 8,
              width: '100%'
            }}>
              <Typography variant="subtitle2" sx={{fontWeight: 'bold'}} html>{t("eat_diary.groups.name")}</Typography>
              <Typography variant="body2" html>{t(!!eat_group_name ? eat_group_name : "eat_diary.groups.none")}</Typography>
              <Button
                id="adjust_eat"
                sx={{fontSize: '0.7rem', marginLeft: 1, float: 'right', marginTop: '-32px'}}
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  setGroupsEatEditing(true);
                }}
                label={t("eat_diary.groups.adjust_cta")}
              />
            </Box>
            {!is_nutri_diary_eat && <Box sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              width: '100%',
              zIndex: 1201,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              height: '56px',
              textAlign: 'center',
              cursor: 'pointer'
            }} onClick={() => {
              setPremiumDiaryEatOpen(true);
            }}>
              <StarBorderPurple500 sx={{marginTop: 1, color: theme.palette.primary.main}} />
            </Box>}

            <Divider sx={{marginTop: 7}} />
          </Grid>;
        }
        if (item.key === 'move_group') {
          return <Grid key={item.key} item xs={12} sx={{position: 'relative'}}>
            <Box sx={{
              zIndex: 1200,
              position: 'absolute',
              top: 8,
              left: 8,
              width: '100%'
            }}>
              <Typography variant="subtitle2" sx={{fontWeight: 'bold'}} html>{t("move_diary.groups.name")}</Typography>
              <Typography variant="body2" html>{t(!!move_group_name ? move_group_name : "move_diary.groups.none")}</Typography>
              <Button
                id="adjust_move"
                sx={{fontSize: '0.7rem', marginLeft: 1, float: 'right', marginTop: '-32px'}}
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  setGroupsMoveEditing(true);
                }}
                label={t("eat_diary.groups.adjust_cta")}
              />
            </Box>
            {!is_nutri_diary_move && <Box sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              width: '100%',
              zIndex: 1201,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              height: '56px',
              textAlign: 'center',
              cursor: 'pointer'
            }} onClick={() => {
              setPremiumDiaryMoveOpen(true);
            }}>
              <StarBorderPurple500 sx={{marginTop: 1, color: theme.palette.primary.main}} />
            </Box>}

            <Divider sx={{marginTop: 7}} />
          </Grid>;
        }
        if (item.key === 'last_entry') {
          // Same row shape as the daynote rows: subtitle2 label, body2 date,
          // italic no_results placeholder — not the old centered one-liner.
          return <Grid key={item.key} item xs={12}>
            <Typography variant="subtitle2" html>{t("shared.terms.last_entry", "Laatste ingave")}</Typography>
            {!!object.module_status?.diary_day && <Typography variant="body2">
              <Dt o={object.module_status?.diary_day} f="DDD" />
            </Typography>}
            {!object.module_status?.diary_day && <Typography variant="body2" sx={{fontStyle: 'italic'}} html>
              {t("coach.diary.no_data_short")}
            </Typography>}
          </Grid>;
        }
        if (item.key === 'daynote_client') {
          return <Grid key={item.key} item xs={12}>
            <Typography variant="subtitle2" html>{t("coach.submenu.detail.daynote_client")}</Typography>
            {!!lastClientNote && <>
              <Typography variant="body2" sx={{
                paddingLeft: '12px',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}>{lastClientNote.note}</Typography>
              <Typography variant="caption" sx={{display: 'block', color: 'text.secondary'}} html>
                {t("coach.submenu.detail.daynote_on", {date: lastClientNote.dayLabel})}
              </Typography>
            </>}
            {!lastClientNote && <Typography variant="body2" sx={{fontStyle: 'italic'}} html>
              {t("shared.terms.no_results", "Er zijn geen resultaten beschikbaar")}
            </Typography>}
          </Grid>;
        }
        if (item.key === 'daynote_coach') {
          return <Grid key={item.key} item xs={12}>
            <Typography variant="subtitle2" html>{t("coach.submenu.detail.daynote_coach")}</Typography>
            {!!lastCoachFeedback && <>
              <Typography variant="body2" sx={{
                paddingLeft: '12px',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}>{lastCoachFeedback.comment}</Typography>
              <Typography variant="caption" sx={{display: 'block', color: 'text.secondary'}} html>
                {t("coach.submenu.detail.daynote_on", {date: lastCoachFeedbackLabel})}
              </Typography>
            </>}
            {!lastCoachFeedback && <Typography variant="body2" sx={{fontStyle: 'italic'}} html>
              {t("shared.terms.no_results", "Er zijn geen resultaten beschikbaar")}
            </Typography>}
          </Grid>;
        }
        return null;
      })}
    </Grid>

    <Base
      name="group_eat"
      hidden={!groupsEatEditing}
      title={t("coach.dialogs.adjust_eat_group.title")}
      content={<Grid container spacing={1}>
        <Grid item xs={12}>
          <Select
            fullWidth
            caption={t("coach.models.clients.group_eat", "Eetdagboek groep")}
            displayEmpty
            id="group_eat"
            value={newEatGroup || ''}
            setValue={(v) => {
              setNewEatGroup(v.toString());
            }}
          >
            <MenuItem value="">{t("eat_diary.groups.none")}</MenuItem>
            <MenuItem value="full">{t("eat_diary.groups.full")}</MenuItem>
            <MenuItem value="mindfull">{t("eat_diary.groups.mindfull")}</MenuItem>
            <MenuItem value="complaints">{t("eat_diary.groups.complaints")}</MenuItem>
            <MenuItem value="move">{t("eat_diary.groups.move")}</MenuItem>
            <MenuItem value="directions">{t("eat_diary.groups.directions")}</MenuItem>
          </Select>
        </Grid>

      </Grid>}
      actions={<DialogActions sx={{borderTop: '1px solid transparent', borderColor: theme.palette.primary.main}}>
        <Btn autoFocus onClick={() => {setGroupsEatEditing(false);}}>{t("coach.general.actions.cancel")}</Btn>
        <Btn onClick={(e) => {
          setGroupsEatEditing(false);
          mutationCustomAction.mutate({formData: {
            object: 'user',
            class_action: 'custom_api_set_group',
            id: object?.id,
            eat: newEatGroup,
            is_eat: 1
          }, group: true});
        }}>{t("coach.general.actions.save")}</Btn>
      </DialogActions>}
      open={groupsEatEditing}
      setOpen={setGroupsEatEditing}
    />

    <Base
      name="group_move"
      hidden={!groupsMoveEditing}
      title={t("coach.dialogs.adjust_move_group.title")}
      content={<Grid container spacing={1}>
        <Grid item xs={12}>
          <Select
            fullWidth
            caption={t("coach.models.clients.group_move", "Beweegdagboek groep")}
            displayEmpty
            id="group_move"
            value={newMoveGroup || ''}
            setValue={(v) => {
              setNewMoveGroup(v.toString());
            }}
          >
            <MenuItem value="">{t("move_diary.groups.none")}</MenuItem>
            <MenuItem value="full">{t("move_diary.groups.full")}</MenuItem>
            <MenuItem value="limited">{t("move_diary.groups.limited")}</MenuItem>
            <MenuItem value="complaints">{t("move_diary.groups.complaints")}</MenuItem>
          </Select>
        </Grid>

      </Grid>}
      actions={<DialogActions sx={{borderTop: '1px solid transparent', borderColor: theme.palette.primary.main}}>
        <Btn autoFocus onClick={() => {setGroupsMoveEditing(false);}}>{t("coach.general.actions.cancel")}</Btn>
        <Btn onClick={(e) => {
          setGroupsMoveEditing(false);
          mutationCustomAction.mutate({formData: {
            object: 'user',
            class_action: 'custom_api_set_group',
            id: object?.id,
            move: newMoveGroup,
            is_move: 1
          }, group: true});
        }}>{t("coach.general.actions.save")}</Btn>
      </DialogActions>}
      open={groupsMoveEditing}
      setOpen={setGroupsMoveEditing}
    />
  </Paper>;
}
